import { SimNaoEnum, simNaoEnumToBoolean } from 'components/form'
import { validateEnderecoCompleto } from 'components/form/field/endereco/validator-endereco'
import { validateEnderecoIndigena } from 'components/form/field/enderecoindigena/validator-enderecoIndigena'
import { justificativasNaoPossuiCpfValidas } from 'components/form/field/select/JustificativaNaoPossuiCpfSelectField'
import { LotacaoAndEstagioSelectFieldModel } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { ResponsavelFamiliarSelectFieldModel } from 'components/form/field/select/ResponsavelFamiliarSelectField/ResponsavelFamiliarSelectField'
import { LotacaoResponsavelFormModel } from 'components/lotacao-responsavel/model-lotacaoResponsavel'
import {
  JustificativaNaoPossuiCpfEnum,
  NacionalidadeEnum,
  RacaCorDbEnum,
  TipoEnderecoEnum,
  TipoLocalNascimentoEnum,
} from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { calculateAge } from 'util/date/calculateAge'
import {
  afterEqualTo,
  alfaNumerico,
  beforeEqualToday,
  cns,
  composeRules,
  cpf,
  createValidator,
  dataNasc,
  differentThan,
  email,
  empty,
  ErrorObject,
  length,
  maxLength,
  microarea,
  minLength,
  nome,
  range,
  required,
  simpleName,
  telefone,
  validate,
} from 'util/validation'
import { tipoEnderecoIndigena } from 'view/cadastro-imovel/model-cadastroImovel'

import {
  MSG_CNS_RESPONSAVEL_FAMILIAR_IGUAL_CIDADAO,
  MSG_CPF_RESPONSAVEL_FAMILIAR_IGUAL_CIDADAO,
} from '../constants-cidadao'
import { CidadaoDocumentoRepetidoArray } from './components/ModalCidadaosDocumentoRepetidos'
import {
  CidadaoAldeadoFormModel,
  CidadaoFormModel,
  CondicoesSaudeAutorreferidasFormModel,
  ContatosFormModel,
  DadosPessoaisFormModel,
  InformacoesSociodemograficasFormModel,
  InformacoesSocioEconomicasFormModel,
  NacionalidadeFormModel,
  ResponsabilidadeAcompanhamentoFormModel,
  SituacaoRuaFormModel,
} from './types/model-cidadao'

const checkCpfCnsChange = (initialCpf: string, initialCns: string, newCpf: string, newCns: string) => {
  if (isUndefinedOrNull(initialCpf) || isUndefinedOrNull(initialCns)) {
    return
  }

  const cpfChanged = initialCpf !== newCpf
  const cnsChanged = initialCns !== newCns

  if (cpfChanged && cnsChanged) {
    return 'Não é permitido apagar ou editar CPF e CNS simultaneamente.'
  }
}

const validateDuplicidade = (
  values: DadosPessoaisFormModel,
  cidadaosDuplicados: CidadaoDocumentoRepetidoArray,
  errors: ErrorObject<DadosPessoaisFormModel>
) => {
  const errorMessage = 'já cadastrado.'

  if (!cidadaosDuplicados || cidadaosDuplicados.length === 0) {
    return
  }

  const formCpf = values.cpf
  const formCns = values.cns
  const formDnv = values.numeroDnv
  const formNis = values.nisPisPasep

  for (const duplicado of cidadaosDuplicados) {
    if (formCpf && duplicado.cpf === formCpf && !errors.cpf) {
      errors.cpf = `CPF ${errorMessage}`
    }
    if (formCns && duplicado.cns === formCns && !errors.cns) {
      errors.cns = `CNS ${errorMessage}`
    }
    if (formDnv && duplicado.numeroDnv === formDnv && !errors.numeroDnv) {
      errors.numeroDnv = `DNV ${errorMessage}`
    }
    if (formNis && duplicado.nisPisPasep === formNis && !errors.nisPisPasep) {
      errors.nisPisPasep = `N° NIS (PIS/PASEP) ${errorMessage}`
    }
  }
}

export const dadosPessoaisCidadaoValidator = (
  tipoLocalNascimento: TipoLocalNascimentoEnum,
  cidadaosDuplicados: CidadaoDocumentoRepetidoArray
) =>
  createValidator<DadosPessoaisFormModel>(
    {
      cpf: cpf,
      cns: cns,
      sexo: [required],
      racaCor: [required],
      nisPisPasep: [length(11)],
      dataNascimento: [required, dataNasc],
      nome: [required, nome(3, 70)],
      nomeSocial: [simpleName, maxLength(70)],
      desejaInformarIdentidadeGenero: [required],
      desejaInformarOrientacaoSexual: [required],
      numeroDnv: [[minLength(11), maxLength(15)]],
    },
    (values: DadosPessoaisFormModel, errors: ErrorObject<DadosPessoaisFormModel>) => {
      if (values.desconheceNomeMae !== true) {
        errors.nomeMae = composeRules([required, nome(3, 70)])(values.nomeMae)
      } else {
        errors.nomeMae = empty(values.nomeMae)
      }

      if (values.desconheceNomePai !== true) {
        errors.nomePai = composeRules([required, nome(3, 70)])(values.nomePai)
      } else {
        errors.nomePai = empty(values.nomePai)
      }

      if (values.racaCor?.value !== RacaCorDbEnum.INDIGENA && values.etnia) {
        errors.etnia = empty(values.etnia)
      }

      if (values.racaCor?.value === RacaCorDbEnum.INDIGENA) {
        errors.etnia = required(values.etnia)
      }

      if (values?.faleceu) {
        errors.dataObito = dataObitoComposedRule(
          values.dataObito,
          values.dataNascimento,
          values.nacionalidadeCidadao.dataEntradaPais,
          values.nacionalidadeCidadao.dataNaturalizacao
        )
      } else {
        errors.dataObito = empty(values.dataObito)
        errors.numeroDeclaracaoObito = empty(values.numeroDeclaracaoObito)
      }

      errors.nacionalidadeCidadao = nacionalidadeValidator(
        values.dataNascimento,
        tipoLocalNascimento,
        values.isCidadaoAldeado
      )(values.nacionalidadeCidadao)

      if (simNaoEnumToBoolean[values.desejaInformarIdentidadeGenero]) {
        errors.identidadeGenero = required(values.identidadeGenero)
      } else {
        errors.identidadeGenero = empty(values.identidadeGenero)
      }

      if (simNaoEnumToBoolean[values.desejaInformarOrientacaoSexual]) {
        errors.orientacaoSexual = required(values.orientacaoSexual)
      } else {
        errors.orientacaoSexual = empty(values.orientacaoSexual)
      }

      errors.cpf = values.stNaoPossuiCpf ? empty(values.cpf) : required(values.cpf) ?? errors.cpf

      errors.justificativaNaoPossuiCpf = values.stNaoPossuiCpf
        ? required(values.justificativaNaoPossuiCpf)
        : empty(values.justificativaNaoPossuiCpf)

      !errors.justificativaNaoPossuiCpf && validateJustificativaValue(errors, values.justificativaNaoPossuiCpf)

      errors.initialCpf = checkCpfCnsChange(values.initialCpf, values.initialCns, values.cpf, values.cns)

      validateDuplicidade(values, cidadaosDuplicados, errors)

      return errors
    }
  )

const validateJustificativaValue = (
  errors: ErrorObject<DadosPessoaisFormModel>,
  justificativa?: JustificativaNaoPossuiCpfEnum
) => {
  if (justificativa && !justificativasNaoPossuiCpfValidas.includes(justificativa)) {
    errors.justificativaNaoPossuiCpf = 'Justificativa inválida'
  }
}

export const nacionalidadeValidator = (
  dataNascimento: string,
  tipoLocalNascimento: TipoLocalNascimentoEnum,
  isCidadaoAldeado: boolean
) =>
  createValidator<NacionalidadeFormModel>(
    {
      nacionalidade: [required],
    },
    (values: NacionalidadeFormModel, errors: ErrorObject<NacionalidadeFormModel>) => {
      if (
        values.nacionalidade === NacionalidadeEnum.BRASILEIRA &&
        tipoLocalNascimento !== TipoLocalNascimentoEnum.ALDEIA &&
        (!isCidadaoAldeado || isMenorQueUmAno(dataNascimento))
      ) {
        errors.municipioNascimento = required(values.municipioNascimento)
      }

      if (values.nacionalidade === NacionalidadeEnum.NATURALIZADA) {
        errors.portariaNaturalizacao = composeRules([required, maxLength(16), alfaNumerico])(
          values.portariaNaturalizacao
        )
        errors.dataNaturalizacao = composeRules([
          required,
          beforeEqualToday,
          checkIsAfterEqualNascimento(values.dataNaturalizacao, dataNascimento),
        ])(values.dataNaturalizacao)
      }

      if (values.nacionalidade === NacionalidadeEnum.ESTRANGEIRA) {
        errors.paisNascimento = required(values.paisNascimento)
        errors.dataEntradaPais = composeRules([
          required,
          beforeEqualToday,
          checkIsAfterEqualNascimento(values.dataEntradaPais, dataNascimento),
        ])(values.dataEntradaPais)
      }

      return errors
    }
  )

export const informacoesSocioEconomicasValidator = () =>
  createValidator<InformacoesSocioEconomicasFormModel>(
    {},
    (values: InformacoesSocioEconomicasFormModel, errors: ErrorObject<InformacoesSocioEconomicasFormModel>) => {
      if (
        !isUndefinedOrNull(values?.alimentosAcabaramAntesTerDinheiroComprarMais) ||
        !isUndefinedOrNull(values?.comeuAlgunsAlimentosDinheiroAcabou)
      ) {
        errors.alimentosAcabaramAntesTerDinheiroComprarMais = required(
          values.alimentosAcabaramAntesTerDinheiroComprarMais
        )
        errors.comeuAlgunsAlimentosDinheiroAcabou = required(values.comeuAlgunsAlimentosDinheiroAcabou)
      }

      return errors
    }
  )

export const informacoesSociodemograficasValidator = () =>
  createValidator<InformacoesSociodemograficasFormModel>(
    {},
    (values: InformacoesSociodemograficasFormModel, errors: ErrorObject<InformacoesSociodemograficasFormModel>) => {
      if (values?.possuiDeficiencia === SimNaoEnum.SIM) {
        errors.deficiencias = required(values.deficiencias)
      }
      if (values?.isAtendimentoSocioeducativo === SimNaoEnum.SIM) {
        errors.tipoAtendimentoSocioeducativo = required(values.tipoAtendimentoSocioeducativo)
        errors.tempoAtendimentoSocioeducativo = required(values.tempoAtendimentoSocioeducativo)
      }

      return errors
    }
  )

export const contatosValidator = (nacionalidade: NacionalidadeEnum, isCidadaoAldeado?: boolean) =>
  createValidator<ContatosFormModel>(
    {
      telefoneCelular: [telefone],
      telefoneResidencial: [telefone],
      telefoneContato: [telefone],
      email: [email],
    },
    (values: ContatosFormModel, errors: ErrorObject<ContatosFormModel>) => {
      if (!errors.telefoneCelular && nacionalidade !== NacionalidadeEnum.ESTRANGEIRA && !isCidadaoAldeado) {
        errors.telefoneCelular = required(values?.telefoneCelular)
      }

      return errors
    }
  )

const validateDoencaCondicoesSaudeaRequired = (
  status: SimNaoEnum | undefined,
  value: any,
  fieldName: keyof CondicoesSaudeAutorreferidasFormModel,
  errors: ErrorObject<CondicoesSaudeAutorreferidasFormModel>
) => {
  if (status === SimNaoEnum.SIM && (!value || value.length === 0)) {
    errors[fieldName] = required(value)
  }
}

const isIdadeForaDaFaixaFertil = (dataNascimento: string): boolean => {
  if (!dataNascimento) return false

  const hoje = new Date()
  const nascimentoDate = new Date(dataNascimento)
  const limiteInferior = new Date(hoje.getFullYear() - 9, hoje.getMonth(), hoje.getDate())
  const limiteSuperior = new Date(hoje.getFullYear() - 60, hoje.getMonth(), hoje.getDate())

  return nascimentoDate > limiteInferior || nascimentoDate < limiteSuperior
}

export const condicoesSaudeAutorreferidasValidator = (dataNascimento: string) =>
  createValidator<CondicoesSaudeAutorreferidasFormModel>(
    {
      maternidadeReferencia: [maxLength(100)],
      causaInternacaoUltimos12Meses: [maxLength(100)],
      plantasMedicinais: [maxLength(100)],
      condicaoSaude1: [maxLength(100)],
      condicaoSaude2: [maxLength(100)],
      condicaoSaude3: [maxLength(100)],
    },
    (values, errors) => {
      if (!values) return errors
      validateDoencaCondicoesSaudeaRequired(values?.stDoencaCardiaca, values.doencaCardiaca, 'doencaCardiaca', errors)
      validateDoencaCondicoesSaudeaRequired(
        values?.stDoencaRespiratoria,
        values.doencasRespiratorias,
        'doencasRespiratorias',
        errors
      )
      validateDoencaCondicoesSaudeaRequired(values?.stProblemaRins, values.doencaRins, 'doencaRins', errors)

      if (isIdadeForaDaFaixaFertil(dataNascimento)) {
        if (values?.stGestante === SimNaoEnum.SIM) {
          errors.stGestante = 'Idade fora da faixa etária permitida.'
        }
        if (values?.maternidadeReferencia) {
          errors.maternidadeReferencia = empty(values.maternidadeReferencia)
        }
      }

      return errors
    }
  )

interface CidadaoValidatorOptions {
  isUsuarioAps?: boolean
  isCadastroOrigemForaAps?: boolean
}

export const cidadaoValidator = (
  cidadaosDuplicadosFiltrados: CidadaoDocumentoRepetidoArray,
  options: CidadaoValidatorOptions = {}
) =>
  createValidator<CidadaoFormModel>(
    {
      statusSituacaoRua: [required],
    },
    (values: CidadaoFormModel, errors: ErrorObject<CidadaoFormModel>) => {
      const { isUsuarioAps = true, isCadastroOrigemForaAps = false } = options
      const isNovoCadastro = !values?.id
      const isCidadaoAldeado = values?.dadosPessoais?.isCidadaoAldeado
      const isModoSimplificadoForaAps =
        !isCidadaoAldeado && !isUsuarioAps && (isNovoCadastro || isCadastroOrigemForaAps)

      errors.dadosPessoais = dadosPessoaisCidadaoValidator(
        values?.cidadaoAldeado?.tipoLocalNascimento,
        cidadaosDuplicadosFiltrados
      )(values?.dadosPessoais)

      if (!isModoSimplificadoForaAps) {
        errors.informacoesSocioEconomicas = informacoesSocioEconomicasValidator()(values?.informacoesSocioEconomicas)
        errors.informacoesSociodemograficas = informacoesSociodemograficasValidator()(
          values?.informacoesSociodemograficas
        )
        errors.situacaoRua = situacaoDeRuaValidator()(values?.situacaoRua)
        errors.condicoesSaudeAutorreferidas = condicoesSaudeAutorreferidasValidator(
          values?.dadosPessoais?.dataNascimento
        )(values?.condicoesSaudeAutorreferidas)
      }
      if (TipoEnderecoEnum.LOGRADOURO === values.tipoEndereco) {
        errors.endereco = validateEnderecoCompleto(values.endereco)
      } else if (tipoEnderecoIndigena.includes(values.tipoEndereco)) {
        errors.enderecoIndigena = validateEnderecoIndigena(values.enderecoIndigena)
      }

      errors.contatos = contatosValidator(
        values?.dadosPessoais?.nacionalidadeCidadao?.nacionalidade,
        values?.dadosPessoais?.isCidadaoAldeado
      )(values?.contatos)

      if (values?.dadosPessoais?.isCidadaoAldeado && values?.cidadaoAldeado) {
        errors.cidadaoAldeado = validateCidadaoAldeado(values)(values?.cidadaoAldeado)
      }

      errors.responsabilidadeAcompanhamento = validateResponsabilidadeAcompanhamento(
        values?.dadosPessoais?.cpf,
        values?.dadosPessoais?.cns,
        {
          skipResponsavelFamiliarValidation: isModoSimplificadoForaAps,
        }
      )(values?.responsabilidadeAcompanhamento)

      return errors
    }
  )

const checkIsAfterEqualNascimento = (data: string, dataNascimento: string) => () =>
  afterEqualTo(data, dataNascimento, 'data nascimento')

const dataObitoComposedRule = (
  dataObito: string,
  dataNascimento: string,
  dataEntradaPais?: string,
  dataNaturalizacao?: string
) => {
  return composeRules([
    required,
    beforeEqualToday,
    checkIsAfterEqualNascimento(dataObito, dataNascimento),
    () => afterEqualTo(dataObito, dataEntradaPais, 'data de entrada no país'),
    () => afterEqualTo(dataObito, dataNaturalizacao, 'data de naturalização'),
  ])(dataObito)
}

export const situacaoDeRuaValidator = () =>
  createValidator<SituacaoRuaFormModel>(
    {
      grauParentescoVisitaFamiliar: [maxLength(100)],
      instituicaoAcompanhamento: [maxLength(100)],
    },
    (values: SituacaoRuaFormModel, errors: ErrorObject<SituacaoRuaFormModel>) => {
      if (values?.possuiAcessoHigienePessoal === SimNaoEnum.SIM) {
        errors.higienePessoal = required(values.higienePessoal)
      }

      return errors
    }
  )

export const validateResponsabilidadeAcompanhamento = (
  cpfCidadao: string | undefined,
  cnsCidadao: string | undefined,
  options: {
    skipResponsavelFamiliarValidation?: boolean
  } = {}
) => {
  const { skipResponsavelFamiliarValidation = false } = options
  return createValidator<ResponsabilidadeAcompanhamentoFormModel>(
    {
      microarea: [microarea],
    },
    (values: ResponsabilidadeAcompanhamentoFormModel, errors: ErrorObject<ResponsabilidadeAcompanhamentoFormModel>) => {
      if (values?.responsavel) {
        errors.responsavel = validateResponsavel()(values?.responsavel)
      }

      if (values?.statusForaArea) {
        errors.microarea = empty(values?.microarea)
      }

      if (skipResponsavelFamiliarValidation) {
        errors.responsavelFamiliar = undefined
        errors.grauParentescoResponsavelFamiliar = undefined
        return errors
      }

      if (values.isResponsavelFamiliar) {
        errors.responsavelFamiliar = empty(values.responsavelFamiliar)
        errors.grauParentescoResponsavelFamiliar = empty(values.grauParentescoResponsavelFamiliar)
      } else {
        const responsavelError = validateResponsavelFamiliar()(values.responsavelFamiliar)

        const cpfResponsavelError = differentThan(
          cpfCidadao,
          MSG_CPF_RESPONSAVEL_FAMILIAR_IGUAL_CIDADAO
        )(values?.responsavelFamiliar?.cpf)

        const cnsResponsavelError = differentThan(
          cnsCidadao,
          MSG_CNS_RESPONSAVEL_FAMILIAR_IGUAL_CIDADAO
        )(values?.responsavelFamiliar?.cns)

        errors.responsavelFamiliar = responsavelError || cpfResponsavelError || cnsResponsavelError
      }

      return errors
    }
  )
}

export const validateResponsavelFamiliar = () => {
  return createValidator<ResponsavelFamiliarSelectFieldModel>({
    cpf: [cpf],
    cns: [cns],
  })
}

export const validateResponsavel = () => {
  return createValidator<LotacaoResponsavelFormModel>(
    {
      lotacao: [required],
    },
    (values: LotacaoResponsavelFormModel, errors: ErrorObject<LotacaoResponsavelFormModel>) => {
      if (values?.lotacao) {
        errors.lotacao = validateLotacao()(values?.lotacao)
      }
      return errors
    }
  )
}

const isMenorQueUmAno = (dataNascimento: string): boolean => {
  if (!dataNascimento) return false
  return dataNascimento && calculateAge(dataNascimento).years < 1
}

export const validateLotacao = () => {
  return createValidator<LotacaoAndEstagioSelectFieldModel>(
    {
      profissional: [required],
      unidadeSaude: [required],
      cbo: [required],
    },
    (_values: LotacaoAndEstagioSelectFieldModel, errors: ErrorObject<LotacaoAndEstagioSelectFieldModel>) => errors
  )
}

export const validateCidadaoAldeado = (cidadaoValues: CidadaoFormModel) => {
  return createValidator<CidadaoAldeadoFormModel>(
    {
      dtReconhecimento: [required, beforeEqualToday],
      nomeTradicional: [maxLength(70)],
      responsavelLegal: [simpleName, maxLength(70)],
      unidadeFunai: [maxLength(40)],
      cadastroUnico: [maxLength(20)],
      dtEmissao: [beforeEqualToday],
      livro: [range(0, 99999999)],
      folha: [range(0, 9999)],
      dtAusencia: [beforeEqualToday],
      dtRetorno: [beforeEqualToday],
    },
    (
      cidadaoAldeadoValues: CidadaoAldeadoFormModel,
      errors: ErrorObject<CidadaoAldeadoFormModel>
    ): ErrorObject<CidadaoAldeadoFormModel> => {
      if (cidadaoAldeadoValues.dtRetorno) {
        errors.dtAusencia = validate(cidadaoAldeadoValues.dtAusencia, required)
      }

      if (!errors?.dtRetorno && cidadaoAldeadoValues.dtAusencia && cidadaoAldeadoValues.dtRetorno) {
        errors.dtRetorno = afterEqualTo(
          cidadaoAldeadoValues.dtRetorno,
          cidadaoAldeadoValues.dtAusencia,
          'data de ausência'
        )
      }

      if (!errors?.dtReconhecimento)
        errors.dtReconhecimento = afterEqualTo(
          cidadaoAldeadoValues.dtReconhecimento,
          cidadaoValues.dadosPessoais.dataNascimento,
          'data de nascimento'
        )

      if (!errors?.dtEmissao)
        errors.dtEmissao = afterEqualTo(
          cidadaoAldeadoValues.dtEmissao,
          cidadaoValues.dadosPessoais.dataNascimento,
          'data de nascimento'
        )

      if (
        NacionalidadeEnum.BRASILEIRA === cidadaoValues.dadosPessoais.nacionalidadeCidadao.nacionalidade &&
        TipoLocalNascimentoEnum.ALDEIA === cidadaoValues.cidadaoAldeado.tipoLocalNascimento &&
        isMenorQueUmAno(cidadaoValues.dadosPessoais.dataNascimento)
      ) {
        errors.aldeiaNascimento = validate(cidadaoAldeadoValues.aldeiaNascimento, required)
        errors.localOcorrencia = validate(cidadaoAldeadoValues.localOcorrencia, required)
      }

      if (!errors.dtAusencia && cidadaoValues.dadosPessoais.dataNascimento && cidadaoAldeadoValues.dtAusencia) {
        errors.dtAusencia = afterEqualTo(
          cidadaoAldeadoValues.dtAusencia,
          cidadaoValues.dadosPessoais.dataNascimento,
          'data de nascimento'
        )
      }

      return errors
    }
  )
}
